/**
 * "Will emails pick this reply?" in the editor: one typed email, or every example the reply lists,
 * tried against the other replies with this reply's unsaved words. Only the choice is worked out:
 * no client is read and no email is built, so a check takes a second or two.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { wizardMagicEmailApi, type EditableReply, type EmailTestResult } from '../api/api';
import { errorText } from './use-magic-email-library';
import { payload } from './use-reply-editor';

export interface ChanceCheck {
  email: string;
  result: EmailTestResult;
}

export function useReplyChance(entryId: string, reply: EditableReply | null) {
  const [email, setEmail] = useState('');
  const [includeDrafts, setIncludeDrafts] = useState(false);

  const checkOne = useCallback(
    (text: string) =>
      wizardMagicEmailApi.testEmail({
        email: text,
        includeDrafts,
        selectionOnly: true,
        editing: reply ? { entryId, content: payload(reply) } : null,
      }),
    [entryId, includeDrafts, reply],
  );

  const checks = useMutation({
    mutationFn: async (emails: string[]): Promise<ChanceCheck[]> => {
      const results: ChanceCheck[] = [];
      for (const text of emails) results.push({ email: text, result: await checkOne(text) });
      return results;
    },
    onError: error => toast.error(`Could not check: ${errorText(error)}`),
  });

  const examples = (reply?.exampleEmails ?? []).map(example => example.trim()).filter(Boolean);

  return {
    email,
    setEmail,
    includeDrafts,
    setIncludeDrafts,
    check: () => email.trim() && checks.mutate([email.trim()]),
    checkExamples: () => examples.length > 0 && checks.mutate(examples),
    exampleCount: examples.length,
    checking: checks.isPending,
    results: checks.data ?? [],
  };
}
